import axios from 'axios';
import {
  ArcElement,
  BarController,
  BarElement,
  Chart,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  PolarAreaController,
  RadialLinearScale,
  ScatterController,
  TimeSeriesScale,
  Tooltip
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { ConnectedRouter } from 'connected-react-router';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { render } from 'react-dom';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { PersistGate } from 'redux-persist/es/integration/react';
import 'chartjs-adapter-moment';

import 'typeface-roboto';

import LoadingSpinner from 'components/composites/LoadingSpinner';
import { ApplGlobalContext, applicationEventEmitter } from 'config/context/ApplicationGlobalContext';
import { initializeSentry } from 'config/sentry';
import App from 'routes/App';
import configureStore from 'store/configureStore';
import history from 'utils/history';

import * as serviceWorker from './serviceWorker';

import './index.less';

Chart.register(
  ArcElement,
  BarController,
  BarElement,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  PolarAreaController,
  RadialLinearScale,
  ScatterController,
  TimeSeriesScale,
  Tooltip,
  annotationPlugin
);

axios.defaults.baseURL = import.meta.env.VITE_API_URL;

const {
  VITE_SENTRY_DSN,
  VITE_ENV,
  VITE_VERSION,
  VITE_GOOGLE_TAG_MANAGER_ID,
  VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID
} = import.meta.env;

// eslint-disable-next-line no-console
console.log(VITE_VERSION);
console.log({ baseUrl: import.meta.env.BASE_URL });

initializeSentry(
  VITE_SENTRY_DSN,
  VITE_ENV,
  VITE_VERSION,
  history
);

TagManager.initialize({
  gtmId: VITE_GOOGLE_TAG_MANAGER_ID as string
});
// Fire pageview 'GA4 - Virtual Page View' tag in GTM (prod). GTM then sends the
// data to Google Analytics for page view tracking.
TagManager.dataLayer({
  dataLayer: {
    event: 'pageview'
  }
});

// Last page visited before the current page.
let entryUrlWithoutOrigin =
  document.location.pathname +
  document.location.search +
  document.location.hash;
let entryPageTitle = document.title;

history.listen((location) => {
  TagManager.dataLayer({
    dataLayer: {
      event: 'pageview',
      entryUrlWithoutOrigin,
      entryPageTitle
    }
  });
  entryUrlWithoutOrigin = location.pathname + location.search + location.hash;
  entryPageTitle = document.title;
});

const { persistor, store } = configureStore(history);

(async () => {
  // https://github.com/launchdarkly/react-client-sdk/issues/130#issuecomment-1656260383
  // The practical identify call is made in enterpriseScaffold/saga.ts.
  console.info('🔽 Before we receive the user detail, we use the empty context to prevent LaunchDarkly from sending out anonymous identify calls.');

  const LDProvider = await asyncWithLDProvider({
    clientSideID: VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID,
    context: {}
  });

  render(
    <Provider store={store}>
      <PersistGate loading={<LoadingSpinner useLogo />} persistor={persistor}>
        <ConnectedRouter history={history}>
          <CompatRouter>
            <LDProvider>
              <ApplGlobalContext.Provider value={{ applicationEventEmitter }}>
                <App />
              </ApplGlobalContext.Provider>
            </LDProvider>
          </CompatRouter>
        </ConnectedRouter>
      </PersistGate>
    </Provider>,
    document.getElementById('farmbot')
  );
})();

serviceWorker.unregister();
